import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import PasswordChangePrompt from "@/components/PasswordChangePrompt";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";

// Lazy-loaded route pages — code-split to shrink the initial JS bundle
const Signup = lazy(() => import("@/pages/Signup"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const ArchivedItems = lazy(() => import("@/pages/ArchivedItems"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const InvoiceDetail = lazy(() => import("@/pages/InvoiceDetail"));
const TradeIQ = lazy(() => import("@/pages/TradeIQ"));
const Settings = lazy(() => import("@/pages/Settings"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PublicInvoice = lazy(() => import("@/pages/PublicInvoice"));
const PublicQuote = lazy(() => import("@/pages/PublicQuote"));
const PublicContract = lazy(() => import("@/pages/PublicContract"));
const PublicChangeOrder = lazy(() => import("@/pages/PublicChangeOrder"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const SubscriptionRequired = lazy(() => import("@/pages/SubscriptionRequired"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const StaffLogin = lazy(() => import("@/pages/StaffLogin"));
const CrewPortal = lazy(() => import("@/pages/CrewPortal"));
const Reports = lazy(() => import("@/pages/Reports"));
const Messages = lazy(() => import("@/pages/Messages"));
const EditProfile = lazy(() => import("@/pages/EditProfile"));
const PortalLogin = lazy(() => import("@/pages/PortalLogin"));
const PortalVerify = lazy(() => import("@/pages/PortalVerify"));
const PortalDashboard = lazy(() => import("@/pages/PortalDashboard"));
const PortalResetPassword = lazy(() => import("@/pages/PortalResetPassword"));
const CRM = lazy(() => import("@/pages/CRM"));
const Pricebook = lazy(() => import("@/pages/Pricebook"));
const ServiceAgreements = lazy(() => import("@/pages/ServiceAgreements"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
import { ThemeProvider } from "@/components/ThemeProvider";
import { SubscriptionProvider } from "@/components/SubscriptionContext";
import FeatureGate from "@/components/FeatureGate";

// Mobile PWA Pages — lazy-loaded
const MobileLayout = lazy(() => import("@/components/mobile/MobileLayout"));
const MobileHome = lazy(() => import("@/pages/mobile/MobileHome"));
const MobileJobs = lazy(() => import("@/pages/mobile/MobileJobs"));
const MobileJobDetail = lazy(() => import("@/pages/mobile/MobileJobDetail"));
const MobileNewJob = lazy(() => import("@/pages/mobile/MobileNewJob"));
const MobileInvoices = lazy(() => import("@/pages/mobile/MobileInvoices"));
const MobileAccount = lazy(() => import("@/pages/mobile/MobileAccount"));
const MobileNotifications = lazy(() => import("@/pages/mobile/MobileNotifications"));
const MobileLogin = lazy(() => import("@/pages/mobile/MobileLogin"));
const MobileOffline = lazy(() => import("@/pages/mobile/MobileOffline"));
const MobileSchedule = lazy(() => import("@/pages/mobile/MobileSchedule"));
const MobileMessages = lazy(() => import("@/pages/mobile/MobileMessages"));
const MobileCustomers = lazy(() => import("@/pages/mobile/MobileCustomers"));
const MobileReports = lazy(() => import("@/pages/mobile/MobileReports"));
const MobileMore = lazy(() => import("@/pages/mobile/MobileMore"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token")); // legacy fallback only
  const [loading, setLoading] = useState(true);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  useEffect(() => {
    // Cookie-based session check: always ask the server who we are.
    // Sends the httpOnly session cookie (withCredentials) plus a legacy Bearer token if one still exists.
    const checkAuth = async () => {
      try {
        const legacyToken = localStorage.getItem("token");
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {},
        });
        setUser(response.data);
        localStorage.setItem("is_authed", "1");
      } catch (e) {
        localStorage.removeItem("token");
        localStorage.removeItem("is_authed");
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { user: userData } = response.data;
    localStorage.setItem("is_authed", "1");
    setUser(userData);
    // Notify SubscriptionContext
    window.dispatchEvent(new Event('tokenChanged'));
    // Technicians with temp password must change it
    if (userData.requires_password_change) {
      setShowPasswordPrompt(true);
    }
    return userData;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API}/auth/signup`, data, { withCredentials: true });
    const { user: userData } = response.data;
    localStorage.setItem("is_authed", "1");
    // Mark as new signup to trigger onboarding
    localStorage.setItem("is_new_signup", "true");
    setUser(userData);
    window.dispatchEvent(new Event('tokenChanged'));
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.debug("Logout error (clearing local state anyway):", e);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("is_authed");
    setToken(null);
    setUser(null);
    window.dispatchEvent(new Event('tokenChanged'));
  };

  const setAuthUser = (userData) => {
    // Session cookie is already set by the server (e.g. Google OAuth /auth/session)
    localStorage.setItem("is_authed", "1");
    setUser(userData);
    window.dispatchEvent(new Event('tokenChanged'));
  };

  const handlePasswordChanged = () => {
    setShowPasswordPrompt(false);
    // Update user state to reflect password change
    if (user) {
      setUser({ ...user, requires_password_change: false });
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, signup, logout, setAuthUser }}>
      {children}
      <PasswordChangePrompt 
        open={showPasswordPrompt} 
        onClose={() => setShowPasswordPrompt(false)}
        onSuccess={handlePasswordChanged}
      />
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if subscription is locked (past_due, suspended, canceled, expired)
  const lockedStatuses = ["past_due", "suspended", "canceled", "expired"];
  if (user.subscription_status && lockedStatuses.includes(user.subscription_status) && user.role !== "admin") {
    return <Navigate to="/subscription-required" replace />;
  }

  // Check if user role is allowed
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "technician") {
      return <Navigate to="/crew" replace />;
    }
    if (user.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AuthCallback />
        </Suspense>
      </ChunkErrorBoundary>
    );
  }

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/staff" element={<StaffLogin />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/invoice/:invoiceId" element={<PublicInvoice />} />
      <Route path="/quote/:quoteId" element={<PublicQuote />} />
      <Route path="/contract/:contractId" element={<PublicContract />} />
      <Route path="/change-order/:changeOrderId" element={<PublicChangeOrder />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/subscription-required" element={<SubscriptionRequired />} />
      
      {/* Customer Portal (public, magic-link auth) */}
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/verify/:token" element={<PortalVerify />} />
      <Route path="/portal/reset-password" element={<PortalResetPassword />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/jobs" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <Jobs />
        </ProtectedRoute>
      } />

      <Route path="/jobs/:jobId" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <JobDetail />
        </ProtectedRoute>
      } />

      <Route path="/schedule" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <Schedule />
        </ProtectedRoute>
      } />

      <Route path="/archived" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <ArchivedItems />
        </ProtectedRoute>
      } />

      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={["owner", "technician"]}>
          <FeatureGate feature="crew_messaging">
            <Messages />
          </FeatureGate>
        </ProtectedRoute>
      } />

      <Route path="/customers" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <FeatureGate feature="crm">
            <CRM />
          </FeatureGate>
        </ProtectedRoute>
      } />

      <Route path="/pricebook" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <FeatureGate feature="pricebook_inventory">
            <Pricebook />
          </FeatureGate>
        </ProtectedRoute>
      } />

      <Route path="/service-agreements" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <FeatureGate feature="service_agreements">
            <ServiceAgreements />
          </FeatureGate>
        </ProtectedRoute>
      } />

      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <FeatureGate feature="pricebook_inventory">
            <Inventory />
          </FeatureGate>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute allowedRoles={["owner", "technician"]}>
          <EditProfile />
        </ProtectedRoute>
      } />
      
      <Route path="/invoices" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <Invoices />
        </ProtectedRoute>
      } />
      
      <Route path="/invoices/:invoiceId" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <InvoiceDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/trade-iq" element={
        <ProtectedRoute allowedRoles={["owner", "technician"]}>
          <FeatureGate feature="ai_foreman">
            <TradeIQ />
          </FeatureGate>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <Settings />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["owner"]}>
          <FeatureGate feature="reports_export">
            <Reports />
          </FeatureGate>
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/crew" element={
        <ProtectedRoute allowedRoles={["technician"]}>
          <CrewPortal />
        </ProtectedRoute>
      } />
      
      {/* Legacy redirect */}
      <Route path="/technician" element={<Navigate to="/crew" replace />} />
      
      {/* Mobile PWA Routes */}
      <Route path="/mobile/login" element={<MobileLogin />} />
      <Route path="/mobile/offline" element={<MobileOffline />} />
      <Route path="/mobile" element={<MobileLayout />}>
        <Route index element={<MobileHome />} />
        <Route path="jobs" element={<MobileJobs />} />
        <Route path="jobs/new" element={<MobileNewJob />} />
        <Route path="jobs/:jobId" element={<MobileJobDetail />} />
        <Route path="invoices" element={<MobileInvoices />} />
        <Route path="schedule" element={<MobileSchedule />} />
        <Route path="messages" element={<MobileMessages />} />
        <Route path="customers" element={<MobileCustomers />} />
        <Route path="reports" element={<MobileReports />} />
        <Route path="more" element={<MobileMore />} />
        <Route path="account" element={<MobileAccount />} />
        <Route path="notifications" element={<MobileNotifications />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

function App() {
  useEffect(() => {
    // Re-arm chunk auto-reload once the app is stable (so a later deploy can recover too)
    const t = setTimeout(() => sessionStorage.removeItem("chunk_reloaded"), 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
